import React, { useState } from 'react';

import { ErrorMessage, HelperMessage } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import TextField from '@atlaskit/textfield';

import { useTestCaseExecutionStatusPageContext } from '../../TestCaseExecutionStatusPageProvider';
import {
  MessageFooterStyled,
  StatusHelperMessageStyled,
  StatusNameEditorContainerStyled,
  StatusNameStyled,
} from './StatusNameEditor.styled';

type Props = {
  onConfirm: (value: string) => void;
  onCancel?: () => void;
  isValid: boolean;
  setIsValid: (value: boolean) => void;
  defaultValue?: string;
  isDefaultEditing?: boolean;
};

const CHARACTER_LIMIT = 24;
const STATUS_UNIQUE_NOTIFICATION = 'Status names have to be unique';
const LENGTH_ERROR = 'Status name cannot be empty';

export default function StatusNameEditor({
  onConfirm,
  onCancel,
  defaultValue = 'New status',
  isValid = true,
  setIsValid,
  isDefaultEditing = false,
}: Props): JSX.Element {
  const { statuses } = useTestCaseExecutionStatusPageContext();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isEditing, setIsEditing] = useState(isDefaultEditing);

  const validate = (value: string): undefined | string => {
    const isStatusNameEmpty = value.trim().length === 0;

    if (isStatusNameEmpty) {
      setIsValid(false);
      return LENGTH_ERROR;
    }

    const isDuplicatedValue =
      value.trim() !== defaultValue.trim() &&
      statuses.some(
        (status) => status.name.toUpperCase() === value.trim().toUpperCase()
      );

    if (isDuplicatedValue) {
      setIsValid(false);
      return STATUS_UNIQUE_NOTIFICATION;
    }

    setIsValid(true);
    return undefined;
  };

  const MessageComponent = isValid ? HelperMessage : ErrorMessage;

  return (
    <InlineEdit
      hideActionButtons
      defaultValue={defaultValue}
      isEditing={isEditing}
      onEdit={() => {
        setIsEditing(true);
      }}
      onConfirm={(value) => {
        onConfirm(value.trim());
        setIsEditing(false);
        setIsValid(true);
      }}
      onCancel={() => {
        setIsEditing(false);
        setIsValid(true);
        onCancel?.();
      }}
      validate={validate}
      readView={() => <StatusNameStyled>{defaultValue}</StatusNameStyled>}
      editView={({ ...fieldProps }) => (
        <StatusNameEditorContainerStyled>
          <TextField
            {...fieldProps}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setErrorMessage(validate(event.target.value));
              fieldProps.onChange(event);
            }}
            isInvalid={!isValid}
            placeholder='Status name'
            autoFocus
            autoComplete='off'
            maxLength={CHARACTER_LIMIT}
            testId='status-text-field'
          />
          <MessageFooterStyled>
            <MessageComponent>
              {isValid ? STATUS_UNIQUE_NOTIFICATION : errorMessage}
            </MessageComponent>
            <StatusHelperMessageStyled isValid={isValid}>
              {fieldProps.value.length}/{CHARACTER_LIMIT}
            </StatusHelperMessageStyled>
          </MessageFooterStyled>
        </StatusNameEditorContainerStyled>
      )}
    />
  );
}
